import { Sun, Moon, Monitor } from 'lucide-react';
import { clsx } from 'clsx';
import { useTranslation } from 'react-i18next';
import { useThemeStore } from '../../store/themeStore';

type Mode = 'light' | 'dark' | 'system';

const CYCLE: Record<Mode, Mode> = { light: 'dark', dark: 'system', system: 'light' };
const ICONS = { light: Sun, dark: Moon, system: Monitor } as const;

/**
 * Bascule de mode compacte — un seul bouton qui fait défiler
 * Clair → Sombre → Système.
 *
 * Destinée aux en-têtes (surface publique, écrans d'entrée) où le sélecteur à
 * trois segments de `DisplayModeToggle` ne tient pas. Le mode courant est porté
 * par l'icône, et rappelé en toutes lettres dans l'infobulle et pour les
 * lecteurs d'écran. Le bouton s'efface pour les thèmes sans variante sombre
 * (Classic, Aurora) : il n'aurait aucun effet visible.
 */
export default function ModeToggle({ className }: { className?: string }) {
  const { t } = useTranslation('settings');
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);
  const supportsDark = useThemeStore((s) => s.theme.supportsDark);

  if (!supportsDark) return null;

  const Icon = ICONS[mode];
  const label = `${t('appearance.mode.title')} : ${t(`appearance.mode.${mode}`)}`;

  return (
    <button
      type="button"
      onClick={() => setMode(CYCLE[mode])}
      title={label}
      aria-label={label}
      className={clsx(
        'p-2 rounded-lg transition-colors pub-muted hover:text-primary-600',
        className
      )}
    >
      <Icon className="w-4 h-4" />
    </button>
  );
}
